import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthBackgroundLayout } from '../layout/AuthBackgroundLayout';
import { PATHS } from '../constants/Paths.constants';
import { authService } from '../services/AuthService';
import { PrimaryButton, TextButton } from '../components/ui/Button/Buttons';

/**
 * VerifyEmailPage - Email verification confirmation page
 */
export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleVerify = async () => {
    if (!token || isVerifying) return;

    setIsVerifying(true);
    setError(null);

    try {
      const data = await authService.verifyEmail(token);
      setSuccess(true);

      // Small delay to show success message
      setTimeout(() => {
        if (data.nextStep === 'payment' && data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
        } else {
          navigate(PATHS.DASHBOARD);
        }
      }, 1500);
    } catch (error: any) {
      if (error?.status === 410) {
        navigate(PATHS.EXPIRED_LINK, {
          replace: true,
          state: { from: 'verifyEmail', token }
        });
        return;
      }

      console.error('Email verification failed:', error);
      setError(
        error?.message ||
          'Verification failed. Please try again or request a new verification email.'
      );
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center">
      <AuthBackgroundLayout>
        <div className="flex flex-col items-center gap-6 w-full">
          {success ? (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-2xl text-purple-900 font-bold text-center">
                Email Verified!
              </p>
              <p className="text-base font-normal text-gray-600 text-center">
                Redirecting you to the login page...
              </p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <p className="text-2xl text-purple-900 font-bold text-left self-stretch">
                Verify Your Email
              </p>

              <p className="text-base font-normal text-gray-950 text-left self-stretch">
                Click the button below to confirm your email address and
                activate your account.
              </p>

              <PrimaryButton
                onClick={handleVerify}
                disabled={isVerifying}
                className="w-full"
              >
                {isVerifying ? 'Verifying...' : 'Verify Email'}
              </PrimaryButton>

              <p className="text-sm font-normal text-gray-950 text-left self-stretch">
                Having trouble?{' '}
                <TextButton
                  size="sm"
                  className="underline-custom px-1.5 py-0"
                  onClick={() => navigate(PATHS.REGISTRATION)}
                >
                  Request a new verification email
                </TextButton>
              </p>
            </>
          )}
        </div>
      </AuthBackgroundLayout>
    </div>
  );
}
